import { parseUserPermissions } from './permissionsUtils';

export const PAGE_PERMISSION_IDS = {
  dashboard: 'dashboard',
  hr: 'hr',
  construction: 'construction',
  document: 'document',
  procurement: 'procurement',
  marketing: 'marketing',
  preconstruction: 'preconstruction',
  directive: 'directive',
  project_office: 'project_office',
  strategic_map: 'strategic_map',
  interactive_map: 'interactive_map',
  cities: 'cities',
  stages: 'stages',
  process: 'process',
  dependencies: 'dependencies',
  feedback: 'feedback',
};

const DEPARTMENT_TO_SCHEDULE_PAGE = {
  hr: PAGE_PERMISSION_IDS.hr,
  construction: PAGE_PERMISSION_IDS.construction,
  document: PAGE_PERMISSION_IDS.preconstruction,
  procurement: PAGE_PERMISSION_IDS.procurement,
  marketing: PAGE_PERMISSION_IDS.marketing,
  preconstruction: PAGE_PERMISSION_IDS.preconstruction,
  tz_construction: PAGE_PERMISSION_IDS.preconstruction,
  'HR отдел': PAGE_PERMISSION_IDS.hr,
  'Строительный отдел': PAGE_PERMISSION_IDS.construction,
  'Отдел документации': PAGE_PERMISSION_IDS.preconstruction,
  'Отдел закупок': PAGE_PERMISSION_IDS.procurement,
  'Отдел маркетинга': PAGE_PERMISSION_IDS.marketing,
  'ТЗ отдел': PAGE_PERMISSION_IDS.preconstruction,
};

export const SCHEDULE_PERMISSION_IDS = [
  PAGE_PERMISSION_IDS.hr,
  PAGE_PERMISSION_IDS.construction,
  PAGE_PERMISSION_IDS.procurement,
  PAGE_PERMISSION_IDS.marketing,
  PAGE_PERMISSION_IDS.preconstruction,
];

const SCHEDULE_PAGE_IDS = new Set(SCHEDULE_PERMISSION_IDS);

/**
 * Страницы с третьим уровнем прав («администратор раздела»). Пока такой уровень
 * нужен только интерактивной карте: он открывает конструктор показателей,
 * пороги цветов и обновление геоданных.
 */
export const ADMIN_CAPABLE_PAGE_IDS = new Set([PAGE_PERMISSION_IDS.interactive_map]);

export function isDirectorScheduleEditBlocked(permId) {
  return SCHEDULE_PAGE_IDS.has(permId) || permId === PAGE_PERMISSION_IDS.directive;
}

function hasExplicitPermissions(perms) {
  return perms.view.length > 0 || perms.edit.length > 0 || (perms.admin || []).length > 0;
}

export function departmentSchedulePageId(user) {
  if (!user?.department) return null;
  return DEPARTMENT_TO_SCHEDULE_PAGE[user.department] || null;
}

/** Чистая логика просмотра страницы (согласовано с usePermissions). */
export function canViewPageForUser(user, pageId) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if ((parseUserPermissions(user.permissions).admin || []).includes(pageId)) return true;
  if (pageId === PAGE_PERMISSION_IDS.feedback) return true;
  if (pageId === PAGE_PERMISSION_IDS.dashboard) return true;
  if (user.role === 'director') return true;

  const deptPage = departmentSchedulePageId(user);
  const perms = parseUserPermissions(user.permissions);
  const explicit = hasExplicitPermissions(perms);

  if (user.role === 'department_user') {
    if (SCHEDULE_PAGE_IDS.has(pageId)) {
      if (deptPage === pageId) return true;
      if (explicit) {
        return perms.view.includes(pageId) || perms.edit.includes(pageId);
      }
      return false;
    }
    if (pageId === PAGE_PERMISSION_IDS.directive) {
      return !!deptPage || (explicit && (perms.view.includes(pageId) || perms.edit.includes(pageId)));
    }
    if (!explicit) return false;
    return perms.view.includes(pageId) || perms.edit.includes(pageId);
  }

  return false;
}

/** Чистая логика редактирования страницы (согласовано с usePermissions). */
export function canEditPageForUser(user, pageId) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if ((parseUserPermissions(user.permissions).admin || []).includes(pageId)) return true;
  if (pageId === PAGE_PERMISSION_IDS.feedback) return true;
  if (pageId === PAGE_PERMISSION_IDS.dashboard) return false;

  const perms = parseUserPermissions(user.permissions);
  const explicit = hasExplicitPermissions(perms);
  const deptPage = departmentSchedulePageId(user);

  if (user.role === 'director') {
    if (SCHEDULE_PAGE_IDS.has(pageId) || pageId === PAGE_PERMISSION_IDS.directive) {
      return false;
    }
    return perms.edit.includes(pageId);
  }

  if (user.role === 'department_user') {
    if (SCHEDULE_PAGE_IDS.has(pageId)) {
      if (!explicit && deptPage === pageId) return true;
      return perms.edit.includes(pageId);
    }
    if (pageId === PAGE_PERMISSION_IDS.directive) {
      return false;
    }
    return perms.edit.includes(pageId);
  }

  return false;
}

/** Права администратора раздела (сейчас — только интерактивная карта). */
export function canAdminPageForUser(user, pageId) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!ADMIN_CAPABLE_PAGE_IDS.has(pageId)) return false;
  const perms = parseUserPermissions(user.permissions);
  return (perms.admin || []).includes(pageId);
}
